/**
 * @lodex-pro/shared/auth/role-codes — RoleCode pinned const + union type.
 *
 * Phase 3 / Plan 02: master spec §6's 10 permission-matrix rows. Distinct
 * from `TENANT_USER_ROLES` in db/schema/enums.ts because that const includes
 * the §35 role (consolidated per DEC-015) which is NOT a column in the §6
 * permission matrix. ROLE_CODES is the §6-only subset that ROLE_PERMISSIONS
 * keys against.
 *
 * Locked: Plan 06's permissionGuard plugin runs `assertRoleCodesHealthy()`
 * at boot — `SELECT count(*)::int FROM default_roles WHERE code = ANY(${ROLE_CODES})`
 * must equal `ROLE_CODES.length` (mirrors Phase 2 D-08's JOB_STATUS_CODES
 * count assertion against job_statuses).
 *
 * Spec: master spec §6, CONTEXT D-06, gsd/DECISIONS_LOG.md DEC-015.
 */

export const ROLE_CODES = [
  'PLATFORM_ADMIN',
  'TENANT_ADMIN',
  'DISPATCH_MANAGER',
  'DISPATCHER',
  'TRUCK_PUSHER',
  'BILLING_MANAGER',
  'BILLING_STAFF',
  'DRIVER',
  'OPERATOR_ADMIN',
  'FOREMAN',
  // Phase 16 / Task 16.4 (DEC-161) — DISPOSAL tenant admin. Seeded into
  // default_roles by Task 16.2's registration-approval flow; wired into the
  // §6 permission matrix here so the auth-resolution pipeline can derive a
  // permissions Set for disposal-tenant logins (previously threw
  // "roleCode not in ROLE_PERMISSIONS" at Redis-cache resolution time).
  // Grants: MANAGE_DISPOSAL_EMAIL_RECIPIENTS (16.4 CRUD on the multi-
  // recipient list) — future disposal-portal endpoints stack here.
  'DISPOSAL_ADMIN',
  // DEC-313 — the four COMBINED OFFICE ROLES. A user holds exactly one role per
  // tenant (now enforced by UNIQUE (user_id, tenant_id), migration 0124), so a
  // person who both dispatches and bills needs a role code that grants both.
  // Each is the EXACT union of its two components and nothing more — see
  // ROLE_PERMISSIONS, where they are built by spreading the component Sets so
  // they cannot drift when a component gains a permission.
  //
  // Corbin accepted explicitly (2026-10-09) that the two MANAGER combinations put
  // create-job -> approve-ticket -> edit-volume -> invoice in one person's hands;
  // the audit trail is the compensating control (DEC-313 records the evidence).
  'DISPATCHER_BILLING_STAFF',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_STAFF',
  'DISPATCH_MANAGER_BILLING_MANAGER',
  // Operator portal rework PR 1 (migration 0200) — operator field user (pumper, well/lease
  // operator, gauger). Created by Paelode; sees only the wells and tanks they select.
  'PUMPER',
] as const;

export type RoleCode = (typeof ROLE_CODES)[number];

/**
 * DEC-313 — the combined office roles, as their own pinned subset.
 *
 * Kept separate from the eleven single roles because the two are NOT
 * interchangeable in code: a combined role's permission set is DERIVED (the
 * union of its components, see ROLE_PERMISSIONS), while a single role's is
 * authored. Anything that has to enumerate "the roles that stack" — the
 * union-equality test, the Team-page picker grouping, the billing-actor check
 * in the invoice audit row — keys off this list rather than a hand-maintained
 * copy.
 */
export const COMBINED_ROLE_CODES = [
  'DISPATCHER_BILLING_STAFF',
  'DISPATCHER_BILLING_MANAGER',
  'DISPATCH_MANAGER_BILLING_STAFF',
  'DISPATCH_MANAGER_BILLING_MANAGER',
] as const;

export type CombinedRoleCode = (typeof COMBINED_ROLE_CODES)[number];

/** A role whose permission set is authored directly in §6, not derived. */
export type SingleRoleCode = Exclude<RoleCode, CombinedRoleCode>;
